<template>
    <form id="ritual-form" @submit.prevent="handleSubmit(!v$.$invalid)" class="h-100">
        <div class="grid">
            <div class="col-12">
                <as-title class="title"
                    :title="$t('rituals.ritual_form.' + (ritual.id ? 'edit_ritual_title' : 'new_ritual_title'))"
                    :size="3" />
            </div>
            <div v-if="ritualAlreadyExisting" class="col-12" :class="previousFollowUpText ? '' : 'mb-3'">
                <Message class="m-0" severity="error">{{ $t('rituals.ritual_form.already_exist') }}</Message>
            </div>
            <div v-if="previousFollowUpText" class="col-12 mb-3">
                <Message class="m-0 preserve-lines" severity="info">{{ previousFollowUpText }}</Message>
            </div>
            <div class="field col-4 pb-0 mb-3">
                <div class="p-float-label">
                    <Dropdown v-model="v$.selectedEntreprise.$model" :options="entreprisesOptions" optionLabel="label"
                        :class="{ 'p-invalid': v$.selectedEntreprise.$invalid && submitted, }" class="w-100"
                        @update:modelValue="showSafetyLeader()" />
                    <label>{{ $t("community.select_company") }}</label>
                </div>
                <as-input-error :errors="v$.selectedEntreprise.$silentErrors" v-show="submitted" />
            </div>
            <div class="field col-2 pb-0 mb-3">
                <div class="p-float-label">
                    <Calendar class="w-100" v-model="v$.selectedMonth.$model" view="month" dateFormat="mm/yy"
                        :class="{ 'p-invalid': v$.selectedMonth.$invalid && submitted }"
                        @update:modelValue="checkExistingRitual()" />
                    <label for="monthPicker">{{ $t('rituals.ritual_form.ritual_select_month_label') }}</label>
                </div>
                <as-input-error :errors="v$.selectedMonth.$silentErrors" v-show="submitted" />
            </div>
            <div class="field col-6 pb-0 mb-3 safety-leader">
                <template v-if="selectedEntrepriseSafetyLeader">
                    <span class="ml-auto mr-3">{{ selectedEntrepriseSafetyLeader.prenom + " " +
                        selectedEntrepriseSafetyLeader.nom }}</span>
                    <img :src="getSelectedEntrepriseSafetyLeaderImage()" />
                </template>
                <template v-else>
                    <span class="ml-auto mr-3"> {{ $t("unknown") }}</span>
                    <img :src="noUserImg" />
                </template>
                <span class="ml-3 p-2 bg-dark color-white">{{ $t('rituals.ritual_form.ritual_safety_leader_label') }}
                </span>
            </div>
            <div class=" field col-12 pb-0 mb-3">
                <div class="p-float-label">
                    <InputText type="text" v-model="v$.libelle.$model" class="w-100"
                        :class="{ 'p-invalid': v$.libelle.$invalid && submitted }" />
                    <label>{{ $t("rituals.ritual_form.ritual_label") }}</label>
                </div>
                <as-input-error :errors="v$.libelle.$silentErrors" v-show="submitted" />
            </div>
            <div class="field col-12 p-0 m-0 pl-3 flex align-items-center">
                <Checkbox v-model="v$.canceled.$model" binary inputId="canceled-checkbox" />
                <label class="mt-2 ml-2" for="canceled-checkbox">
                    {{ $t('rituals.ritual_form.ritual_canceled_label') }}
                </label>
            </div>
            <div class="field col-12 p-0 m-0 pl-3 flex align-items-center">
                <Checkbox v-model="v$.delayed.$model" binary inputId="delayed-checkbox" />
                <label class="mt-2 ml-2" for="delayed-checkbox">
                    {{ $t('rituals.ritual_form.ritual_delayed_label') }}
                </label>
            </div>
            <div class="field col-4">
                <Dropdown v-model="selectedBloc" :options="blocs" optionLabel="libelle"
                    :placeholder="$t('rituals.ritual_form.ritual_dropdown_bloc_label')" class="w-100"
                    @update:model-value="setFilteredSubjects(selectedBloc)" />
            </div>
            <div class="field col-4">
                <Dropdown v-model="selectedSubject" :options="filteredSubjects" optionLabel="libelle"
                    :placeholder="$t('rituals.ritual_form.ritual_subject_dropdown_bloc_label')" class="w-100" />
            </div>
            <div class="field col-4">
                <Button :label="$t('rituals.ritual_form.ritual_subject_add_subject_button_label')"
                    class="button-default ml-2" @click="addSubject" :disabled="!selectedSubject" />
            </div>
        </div>
        <!-- Liste des blocs sélectionnés -->
        <div class="grid">
            <div v-for="(sujet, sujetIndex) in ritual.rituelSujet" :key="sujet.idSujet" class="col-12">
                <Card class="bloc-card">
                    <template #header>
                        <div class="flex w-100 p-2" :class="[isSubjectExist(sujet) ? 'bg-primary-sliced' : 'bg-error']">
                            <span class="bloc-index">{{ sujetIndex + 1 }}</span>
                            <as-title class="title color-white pl-5" :title="sujet.libelle" :size="4" />
                            <div class="flex align-items-center ml-auto mr-3">
                                <Checkbox v-model="sujet.ficheBu" :binary="true"
                                    :inputId="'ficheBu-checkbox-' + sujetIndex" @change="checkAlreadyChecked" />
                                <label class=" ml-2" :for="'ficheBu-checkbox-' + sujetIndex">
                                    {{ $t('rituals.ritual_form.ritual_bloc_is_fiche_bu_label') }}
                                </label>
                            </div>
                            <div class="flex align-items-center mr-5">
                                <Checkbox v-model="sujet.mustRead" :binary="true"
                                    :inputId="'mustRead-checkbox-' + sujetIndex" />
                                <label class=" ml-2" :for="'mustRead-checkbox-' + sujetIndex">
                                    {{ $t('rituals.ritual_form.ritual_bloc_must_read_label') }}
                                </label>
                            </div>
                            <i class="pi pi-times-circle ml-5 bloc-remove-icon" style="font-size: 28px;"
                                @click="showConfirmModalRemoveSubject(sujet)"></i>
                        </div>
                    </template>
                    <template #content class="p-0">
                        <Chip v-if="!isSubjectExist(sujet)"
                            :label="$t('rituals.ritual_form.ritual_warning_bloc_removed')" class="mr-2 mb-3 bg-error" />
                        <div class="grid">
                            <div class="col-8">
                                <TinyMceEditor v-model="sujet.note" />
                            </div>
                            <div class="field col-4">
                                <AsFileUpload v-model="sujet.fichiers" :fichiers="sujet.fichiers"
                                    :maxFileSize="maxFileUpload" :chooseLabel="$t('rituals.ritual_form.choose_button')"
                                    :multiple="true" />
                            </div>
                        </div>
                    </template>
                </Card>
            </div>
        </div>
        <div class="grid">
            <div class="col-12 pb-0 mb-0">
                <as-title class="title" :title="$t('rituals.ritual_form.textaera_label')" :size="5" />
            </div>
            <div class="col-12 pt-0 mt-0">
                <Textarea type="text" v-model="v$.text.$model" :autoResize="true" rows="6" cols="30" class="w-100" />
                <as-input-error :errors="v$.text.$silentErrors" v-show="submitted" />
            </div>
        </div>
        <div class="grid">
            <div class="col-12 pb-0 mb-0">
                <as-title class="title" :title="$t('rituals.ritual_form.follow_label')" :size="5" />
            </div>
            <div class="col-12 pt-0 mt-0">
                <Textarea type="text" v-model="v$.followUpText.$model" :autoResize="true" rows="6" cols="30"
                    class="w-100" />
                <as-input-error :errors="v$.followUpText.$silentErrors" v-show="submitted" />
            </div>
        </div>
        <div class="grid fixed-bottom">
            <div class="col flex justify-content-end">
                <Button :label="$t('form.cancel_button')" class="button-default"
                    :icon="isSending ? 'pi pi-spin pi-spinner' : 'pi pi-times'" @click="onClose"
                    :disabled="isSending" />
                <Button type="submit" :label="$t('form.submit_button')" class="button-primary ml-2"
                    :icon="isSending ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
                    :disabled="isSending || ritualAlreadyExisting" />
            </div>
        </div>
    </form>

    <as-confirm-modal :isVisible="showRemoveSubjectConfirmModal"
        :modalMessage="$t('rituals.ritual_form.ritual_remove_subject_confirm_message')" @onConfirm="removeSubject"
        @onCancel="cancelConfirmModalRemoveSubject" />

    <as-confirm-modal :isVisible="showSubjectAlert"
        :modalMessage="$t('rituals.ritual_form.ritual_already_three_selected_subject_message')"
        @onConfirm="this.showSubjectAlert = false" :isCancelable="false" />

</template>

<script>

import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { ref } from "vue";
import { mapGetters } from "vuex";

import AsTitle from "../Elements/AsTitle.vue";
import AsFileUpload from "../Elements/AsFileUpload.vue";
import AsInputError from "../Forms/AsInputError.vue"
import AsConfirmModal from "@/components/Modals/AsConfirmModal.vue";
import TinyMceEditor from "@/components/Editor/TinyMceEditor.vue";

import { bufferToJpegBase64 } from "../../util";
import { saveRitual, checkExistingRitual, checkFollowUpText } from "../../services/ritualServiceApi";
import apiEventTypes from "@/constantes/apiEventTypes";
import config from "../../config";
import fichiersServiceApi from "../../services/fichiersServiceApi";
import { getTinyMceConfig } from "../../config/tinymce.config";


import Button from "primevue/button";
import Calendar from "primevue/calendar";
import Card from "primevue/card";
import Column from "primevue/column";
import Chip from 'primevue/chip';
import Checkbox from "primevue/checkbox";
import DataTable from "primevue/datatable";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Message from 'primevue/message';

import AsModal from "../Modals/AsModal.vue";
import moment from "moment";

export default {
    name: "RitualForm",
    components: {
        AsTitle,
        AsFileUpload,
        AsInputError,
        AsConfirmModal,
        AsModal,
        Button,
        Calendar,
        Card,
        Column,
        Chip,
        Checkbox,
        DataTable,
        Dropdown,
        InputText,
        Textarea,
        Message,
        TinyMceEditor,
    },
    props: {
        ritual: Object,
        blocs: [],
        entreprises: [],
    },
    setup() {
        const selectedBloc = ref(null);
        const selectedSubject = ref(null);
        const v$ = useVuelidate();
        return { v$, selectedBloc, selectedSubject };
    },
    mixins: [fichiersServiceApi],
    watch: {
        ritual(ritual) {
            const selectedEntreprise = this.entreprises.find(e => e.id === ritual.idEntreprise)
            this.selectedEntreprise = selectedEntreprise ? {
                label: `${selectedEntreprise.nom}`,
                value: selectedEntreprise.id,
            } : null

            this.selectedMonth = ritual && ritual.year ? new Date(ritual.year, ritual.month - 1, 1) : null
            this.libelle = ritual.libelle ?? ''
            this.text = ritual.champsLibre ?? ''
            this.followUpText = ritual.suivi ?? ''
            this.canceled = ritual.canceledBySafetyLeader ?? false
            this.delayed = ritual.delayed ?? false

            this.showSafetyLeader()

            if (ritual.rituelSujet) {
                const selectedSubjectIds = ritual.rituelSujet.map(e => e.idSujet)
                this.selectedSubjects = this.flattenSubjects.filter(e => selectedSubjectIds.includes(e.id))
            }
        },
        blocs(blocs) {
            this.flattenSubjects = blocs.flatMap(e => e.sujets)
        }
    },
    data() {
        return {
            showRemoveSubjectConfirmModal: false,
            subjectToRemove: null,
            isSending: false,
            isEntrepriseBusy: false,
            flattenSubjects: [],
            filteredSubjects: [],
            selectedEntrepriseSafetyLeader: null,
            selectedSubjects: [],
            submitted: false,
            maxFileUpload: config.max_file_upload,
            noUserImg: new URL("../../assets/images/no-user-image.jpg", import.meta.url).href,
            configTinyMce: getTinyMceConfig(
                this.translateToEditor
            ),
            ritualAlreadyExisting: false,
            previousFollowUpText: '',
            /* Form Data */
            selectedEntreprise: null,
            selectedMonth: null,
            libelle: '',
            text: '',
            followUpText: '',
            canceled: false,
            delayed: false,
            showSubjectAlert: false,
        }
    },
    validations() {
        return {
            libelle: { required },
            selectedEntreprise: { required },
            selectedMonth: { required },
            canceled: {},
            delayed: {},
            text: {},
            followUpText: {}
        }
    },
    emits: ["onClose", "onSubmit"],

    computed: {
        ...mapGetters({
            user: "getUser",
        }),
        entreprisesOptions() {
            return this.entreprises.map((el) => {
                return {
                    label: `${el.nom}`,
                    value: el.id,
                };
            });
        },
    },
    methods: {
        handleSubmit(isFormValid) {
            this.submitted = true;
            if (!isFormValid) { return }
            const selectedMonth = new Date(this.selectedMonth)
            const ritual = {
                id: this.ritual.id,
                idEntreprise: this.selectedEntreprise.value,
                year: selectedMonth.getFullYear(),
                month: selectedMonth.getMonth() + 1,
                canceledBySafetyLeader: this.canceled,
                delayed: this.delayed,
                libelle: this.libelle,
                champsLibre: this.text,
                suivi: this.followUpText,
                rituelSujet: this.ritual.rituelSujet.map(e => {
                    return {
                        id: e.id,
                        idSujet: e.idSujet,
                        sujet: e.libelle,
                        ficheBu: e.ficheBu,
                        mustRead: e.mustRead,
                        note: e.note,
                        fichiers: e.fichiers.filter(f => f.toDelete),
                    }
                })
            }
            this.saveRitual(ritual)
            this.submitted = false;
        },
        setFilteredSubjects(currentBloc) {
            if (!currentBloc) { return [] }
            const bloc = this.blocs.find(e => e.id === currentBloc.id)
            if (!bloc) { return [] }
            const selectedSubjectIds = this.selectedSubjects.map(e => e.id)
            const subjects = [...bloc.sujets].filter(e => !selectedSubjectIds.includes(e.id))
            if (this.selectedSubject && !subjects.find(e => e.id === this.selectedSubject.id)) {
                this.selectedSubject = null
            }
            this.filteredSubjects = subjects
        },
        onClose() {
            this.resetFormValue();
            this.$emit("onClose");
        },
        isSubjectExist(subject) {
            return this.flattenSubjects.find(e => e.id === subject.idSujet)
        },
        addSubject() {
            if (this.selectedBloc && this.selectedSubject) {
                this.selectedSubjects.push(this.selectedSubject)
                this.ritual.rituelSujet.push({
                    id: 0,
                    libelle: this.selectedBloc.libelle + ' - ' + this.selectedSubject.libelle,
                    idSujet: this.selectedSubject.id,
                    ficheBu: false,
                    mustRead: false,
                    note: '',
                    fichiers: [],
                })
                this.selectedSubject = null
                this.setFilteredSubjects(this.selectedBloc)
            }
        },
        showConfirmModalRemoveSubject(subject) {
            this.showRemoveSubjectConfirmModal = true
            this.subjectToRemove = subject
        },
        cancelConfirmModalRemoveSubject() {
            this.showRemoveSubjectConfirmModal = false
            this.subjectToRemove = null
        },
        removeSubject() {
            this.showRemoveSubjectConfirmModal = false
            if (!this.subjectToRemove) { return }
            const indexOf = this.ritual.rituelSujet.findIndex(e => e.idSujet === this.subjectToRemove.idSujet)
            if (indexOf >= 0) { this.ritual.rituelSujet.splice(indexOf, 1) }
            const indexOfSelectedSubjects = this.selectedSubjects.findIndex(e => e.id === this.subjectToRemove.idSujet)
            if (indexOfSelectedSubjects >= 0) { this.selectedSubjects.splice(indexOfSelectedSubjects, 1) }
            this.setFilteredSubjects(this.selectedBloc)
        },
        showSafetyLeader() {
            this.checkExistingRitual()
            this.checkFollowUpText()
            if (!this.selectedEntreprise) { return }
            const enterprise = this.entreprises.find(e => e.id === this.selectedEntreprise.value)
            this.selectedEntrepriseSafetyLeader = enterprise && enterprise.safetyLeader ?
                enterprise.safetyLeader : null
        },
        getSelectedEntrepriseSafetyLeaderImage() {
            if (
                this.selectedEntrepriseSafetyLeader &&
                this.selectedEntrepriseSafetyLeader.photo &&
                this.selectedEntrepriseSafetyLeader.photo.data.length > 0
            ) {
                return bufferToJpegBase64(this.selectedEntrepriseSafetyLeader.photo.data);
            } else {
                return this.noUserImg;
            }
        },
        async saveRitual(data) {
            this.isSending = true
            const savedRitual = await saveRitual(data)
            this.isSending = false
            document.dispatchEvent(
                new CustomEvent(savedRitual ? apiEventTypes.API_SUCCESS : apiEventTypes.API_ERROR, {
                    detail: {
                        title: this.$t(`popup_messages.${savedRitual ? 'success_title' : 'default_error_title'}`),
                        message: this.$t(`popup_messages.${savedRitual ? 'success_created_message' : 'default_error_message'}`),
                    },
                })
            )
            if (savedRitual) {
                this.$emit("onSubmit", savedRitual, this.ritual);
                this.resetFormValue();
            }
        },
        async checkExistingRitual() {
            this.ritualAlreadyExisting = false
            if (this.selectedEntreprise && this.selectedMonth) {
                const formattedDate = moment(this.selectedMonth).format('YYYY-MM')
                const [year, month] = formattedDate.split('-')
                this.ritualAlreadyExisting = await checkExistingRitual(this.ritual?.id || 0, this.selectedEntreprise.value, year, month)
            }
        },
        async checkFollowUpText() {
            this.previousFollowUpText = ''
            const ritualId = this.ritual?.id || 0
            if (this.selectedEntreprise && ritualId === 0) {
                this.previousFollowUpText = await checkFollowUpText(ritualId, this.selectedEntreprise.value)
            }
        },
        checkAlreadyChecked(e) {
            const checked = (this.ritual?.rituelSujet || []).filter(e => e.ficheBu)
            this.showSubjectAlert = checked.length > 3
        },
        resetFormValue() {
            this.ritual.libelle = "";
            this.ritual.rituelSujet = [];
            this.selectedEntreprise = null
            this.selectedMonth = null
            this.selectedEntrepriseSafetyLeader = null
            this.submitted = false;
            this.ritualAlreadyExisting = false;
        }
    },
};
</script>
<style lang="scss">
@import "../../assets/styles/colors.scss";

#ritual-form {
    .safety-leader {
        display: flex;
        align-items: center;

        >span {
            border-radius: 15px;
        }

        img {
            width: 40px;
            height: 40px;
            border-radius: 20px;
        }
    }

    .preserve-lines {
        white-space: pre-line;
    }
}

.bloc-card {
    position: relative;

    .bloc-remove-icon:hover {
        cursor: pointer;
        color: rgb(224, 224, 224);
    }

    .bloc-index {
        position: absolute;
        top: -10px;
        left: -10px;
        width: 40px;
        height: 40px;
        border-radius: 25px;
        margin: auto;
        background: $dark_font;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.2rem;
        font-weight: bold;
    }

    .p-card-body {
        .p-card-content {
            padding: 0;
        }
    }
}
</style>
